import { useStore } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useRef } from 'react'

import LandingZoneMigrationApi from '@api/landingZones/migration'

import * as landingZoneMigrationMapping from '@store/landingZones/Migration/action'
import * as landingZoneMigrationMappingType from '@store/landingZones/Migration/types'

import { useApiError } from '@services/hooks/ApiError'
import { useAppLoader } from '@services/hooks/AppLoader'
import { useNavigate } from 'react-router-dom'

import { createId } from '@services/utils'
import { generateUrl } from '@services/routes'
import { ROUTE_LANDING_ZONES } from '@features/routes'

export const LZ_INTERNET = 'LzInternet'
export const LZ_INTERNET_MAIN = 'LzInternetMain'

export const useLandingZoneMigration = () => {
    const INTRO = 'intro'
    const INTERNET = 'internet'
    const INTERLZ = 'inter'
    const MAIN = 'main'
    const VALIDATION_INTERNET = 'validationInternet'
    const VALIDATION_MAIN = 'validationMain'
    const VALIDATION = 'validation'
    const FINISHED = 'finished'
    const MAINTENANCE = 'maintenance'

    const NEXT = 'next'
    const PREVIOUS = 'previous'

    const NOT_MIGRATED = 'NOT_MIGRATED'
    const ROLLBACKED_INTERLZ = 'ROLLBACKED_INTERLZ'
    const IN_PROGRESS_INTERLZ = 'IN_PROGRESS_INTERLZ'
    const MIGRATED_INTERLZ = 'MIGRATED_INTERLZ'
    const ROLLBACKED_INTERNET = 'ROLLBACKED_INTERNET'
    const IN_PROGRESS_INTERNET = 'IN_PROGRESS_INTERNET'
    const MIGRATED_INTERNET = 'MIGRATED_INTERNET'
    const ROLLBACKED_MAIN = 'ROLLBACKED_MAIN'
    const IN_PROGRESS_MAIN = 'IN_PROGRESS_MAIN'
    const MIGRATED_MAIN = 'MIGRATED_MAIN'
    // const MIGRATED = 'MIGRATED'

    const STEPS = useRef({
        NOT_MIGRATED: INTERLZ,
        ROLLBACKED_INTERLZ: INTERLZ,
        IN_PROGRESS_INTERLZ: INTERLZ,
        MIGRATED_INTERLZ: INTERNET,
        ROLLBACKED_INTERNET: INTERNET,
        IN_PROGRESS_INTERNET: INTERNET,
        MIGRATED_INTERNET: MAIN,
        ROLLBACKED_MAIN: MAIN,
        IN_PROGRESS_MAIN: MAIN,
        MIGRATED_MAIN: VALIDATION,
        MIGRATED: FINISHED
    })

    const IN_PROGRESS = useRef([
        IN_PROGRESS_INTERLZ,
        IN_PROGRESS_INTERNET,
        IN_PROGRESS_MAIN
    ])

    const COMPLETED = useRef([
        MIGRATED_MAIN
    ])

    const LOAD_LOGS = useRef([
        MIGRATED_INTERNET,
        ROLLBACKED_MAIN
    ])

    const MIGRATION_STEP = useRef({
        next: {
            [NOT_MIGRATED]: 'inter-landing-zone-connections',
            [ROLLBACKED_INTERLZ]: 'inter-landing-zone-connections',
            [MIGRATED_INTERLZ]: 'internet',
            [ROLLBACKED_INTERNET]: 'internet',
            [MIGRATED_INTERNET]: 'main',
            [ROLLBACKED_MAIN]: 'main',
            [MIGRATED_MAIN]: 'main'
        },
        previous: {
            [MIGRATED_INTERLZ]: 'inter-landing-zone-connections',
            [ROLLBACKED_INTERNET]: 'inter-landing-zone-connections',
            [MIGRATED_INTERNET]: 'internet',
            [ROLLBACKED_MAIN]: 'internet',
            [MIGRATED_MAIN]:'main'
        }
    })

    const MODES = useRef({
        [LZ_INTERNET]: LZ_INTERNET,
        [LZ_INTERNET_MAIN]: LZ_INTERNET_MAIN
    })

    const defaultLzInternetSteps = [
        { id: 'inter', label: 'Inter Landing Zone'},
        { id: 'internet', label: 'Internet'}
    ]

    const defaultLzInternetMainSteps = [
        { id: 'inter', label: 'Inter Landing Zone'},
        { id: 'internet', label: 'Internet'},
        { id: 'main', label: 'Main'}
    ]

    const store = useStore()
    const landingZoneMigrationAction = bindActionCreators(landingZoneMigrationMapping, store.dispatch)

    const { hide: hideLoader, show: showLoader } = useAppLoader()
    const { handleError: apiError } = useApiError()

    const navigate = useNavigate()
    const loaderCallback = useRef(null)
    const defaultLogs = {nsg: [], app: []}

    const loadedLz = useRef(null)
    const inProgress = useRef(false)
    const displayFooter = useRef(false)
    const isCompleted = useRef(false)
    const step = useRef(null)
    const mode = useRef(null)
    const interLzs = useRef([])
    const defaultRoutes = useRef([])
    const logs = useRef(defaultLogs)
    const status = useRef(null)
    const steps = useRef([])

    const haveMigration = useRef(false)

    const getData = (index = null) => {
        if (!index) return store.getState().landingZoneMigration
        return store.getState().landingZoneMigration[index]
    }

    const redirectToLzList = (message) => {
        apiError(message)
        navigate(generateUrl(ROUTE_LANDING_ZONES))
    }

    const readStatus = () => {
        const data = getData('data')

        status.current = data.status
        logs.current = defaultLogs
        step.current = (data.maintenance) ? MAINTENANCE : false || (STEPS.current[status.current] ?? null)
        mode.current = MODES.current[data.lzType] ?? null

        steps.current = (mode.current === LZ_INTERNET)
            ? defaultLzInternetSteps : defaultLzInternetMainSteps

        if (!step.current || !mode.current) return redirectToLzList('Reading migration status error !')

        inProgress.current = IN_PROGRESS.current.includes(status.current)
        isCompleted.current = COMPLETED.current.includes(status.current)
        displayFooter.current = false

        if (status.current === 'MIGRATED_INTERNET' && mode.current === LZ_INTERNET) {
            displayFooter.current = true
            step.current = VALIDATION_INTERNET
        }
        else if (status.current === 'MIGRATED_MAIN' && mode.current === LZ_INTERNET_MAIN) {
            displayFooter.current = true
            step.current = VALIDATION_MAIN
        }

        if (status.current === 'NOT_MIGRATED' || status.current === 'ROLLBACKED_INTERLZ') return loadInterLzs(true)
        if (LOAD_LOGS.current.includes(status.current) && mode.current === LZ_INTERNET_MAIN) return loadLogs(true)

        apply()
    }

    const loadDefaultRoute = (setAll = false) => {
        LandingZoneMigrationApi.loadDefaultRoute(loadedLz.current, {
            success: (result) => {
                const data = result?.data ?? []
                defaultRoutes.current = false
                for (let i = 0; i < data.length; i++) {
                    if (data[i]?.defaultRoute?.isCustomRoute)
                        defaultRoutes.current = true ; break
                }

                if (setAll) apply()
                else landingZoneMigrationAction.setDefaultRoutes(defaultRoutes.current, loaderCallback.current)
            },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? 'Reading migration default routes error from API !') }
        })
    }

    const loadInterLzs = (setAll = false) => {
        LandingZoneMigrationApi.loadInterLzs(loadedLz.current, {
            success: (result) => {
                if (setAll) {
                    interLzs.current = result?.data?.data ?? []
                    loadDefaultRoute(setAll)
                }
                else landingZoneMigrationAction.setInterLzs(result?.data?.data ?? [], loadDefaultRoute)
            },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? 'Reading migration inter-landingzones error from API !') }
        })
    }

    const loadLogs = (setAll = false) => {
        if (setAll) return loadAppLogs(setAll, () => { loadFlowLogs(setAll, apply) })
        loadAppLogs(setAll, () => { loadFlowLogs(setAll, hideLoader) })
    }

    const loadAppLogs = (setAll = false, callback = null) => {
        LandingZoneMigrationApi.loadAppLogs(loadedLz.current, {
            success: (result) => {
                const newLogs = computeApiLogs(result?.data ?? [])
                if (setAll) {
                    logs.current.app = newLogs
                    callback()
                } else landingZoneMigrationAction.setLogs(newLogs, landingZoneMigrationMappingType.MODE_LOG_APP, callback)
            },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? 'Reading migration APP gateway error from API !') }
        })
    }

    const loadFlowLogs = (setAll = false, callback = null) => {
        LandingZoneMigrationApi.loadFlowLogs(loadedLz.current, {
            success: (result) => {
                const newLogs = computeApiLogs(result?.data ?? [])
                if (setAll) {
                    logs.current.nsg = newLogs
                    callback()
                } else landingZoneMigrationAction.setLogs(newLogs, landingZoneMigrationMappingType.MODE_LOG_NSG, callback)
            },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? 'Reading migration flow logs error from API !') }
        })
    }

    const computeApiLogs = (data) => {
        const newLogs = []

        if (data.length > 0) {
            for (let i = 0; i < data.length; i++)
                newLogs.push({id: createId(10), ...data[i]})
        }

        return newLogs
    }

    const apply = () => {
        landingZoneMigrationAction.setSelected({
            inProgress: inProgress.current,
            mode: mode.current,
            step: step.current,
            interLzs: interLzs.current,
            defaultRoutes: defaultRoutes.current,
            displayFooter: displayFooter.current,
            isCompleted: isCompleted.current,
            lzId: loadedLz.current,
            logs: logs.current,
            status: status.current,
            steps: steps.current
        }, () => {
            if (loaderCallback.current) loaderCallback.current()
            if (haveMigration.current && !loaderCallback.current) {
                hideLoader() ; haveMigration.current = false
            }
        })
    }

    const getStatus = (id = null, loader = false) => {
        if (!id) return null
        if (loader) showLoader(' ')

        loadedLz.current = id
        loaderCallback.current = (loader) ? hideLoader : null

        LandingZoneMigrationApi.migrationStatus(loadedLz.current, {
            success: (result) => { landingZoneMigrationAction.populate(result?.data?.data ?? {}, readStatus) },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? 'Reading migration status error from API !') }
        })
    }

    const migrate = (lzId, nextStep, action) => {
        const journeyType = (mode === 'next') ? 'Migration' : 'Rollback'

        LandingZoneMigrationApi.action(lzId, nextStep, action, {
            success: () => {
                haveMigration.current = true
                getStatus(loadedLz.current, true)
            },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? `${journeyType} API error !`) }
        })
    }

    const terminate = (lzId) => {
        LandingZoneMigrationApi.terminate(lzId, {
            success: () => { getStatus(loadedLz.current, true) },
            error: (result) => { redirectToLzList(result?.data?.data?.errors[0] ?? `Migration failed !`) }
        })
    }

    const getNextStep = (mode = NEXT) => {
        const data = getData('selected')
        loadedLz.current = data.lzId

        const journeyType = (mode === NEXT) ? 'migration' : 'rollback'

        const nextStep = MIGRATION_STEP.current[mode][data.status]
        if (!nextStep) return redirectToLzList(`Error on ${journeyType} user journey !`)

        loaderCallback.current = hideLoader
        showLoader(' ')

        return nextStep
    }

    const handleNext = () => {
        const data = getData('selected')
        const nextStep = getNextStep()

        if (data.step === INTRO) {
            loaderCallback.current = () => { landingZoneMigrationAction.setStep(INTERLZ, hideLoader) }
            return loadInterLzs()
        }

        if ((data.status === MIGRATED_INTERNET && data.mode === LZ_INTERNET)
        || (data.status === MIGRATED_MAIN && data.mode === LZ_INTERNET_MAIN))
            terminate(loadedLz.current)
        else migrate(loadedLz.current, nextStep, 'migrate')
    }

    const handlePrevious = () => {
        const nextStep = getNextStep(PREVIOUS)

        migrate(loadedLz.current, nextStep, 'rollback')
    }

    const handleRefreshMainLogs = () => {
        const data = getData('selected')
        loadedLz.current = data.lzId

        showLoader(' ')
        loadLogs()
    }

    return { getStatus, handleNext, handlePrevious, handleRefreshMainLogs }
}